import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useLegacyAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export const CreateTaskCommentSchema = z.object({
  id: z.union([z.string(), z.number()]),
  comment: z.string(),
});

export type CreateTaskCommentVariables = z.infer<typeof CreateTaskCommentSchema>;

export async function createTaskCommentFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CreateTaskCommentVariables,
) {
  const instance = await axiosInstance;

  return instance.post(`tasks/${data.id}/comments`, CreateTaskCommentSchema.parse(data));
}

export function useCreateTaskComment() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, CreateTaskCommentVariables>({
    mutationFn: data => createTaskCommentFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['comments'],
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    CreateTaskCommentVariables,
    'createTaskComment'
  >('createTaskComment', mutation);
}
